import type { NodeTypes, ReactFlowInstance } from "@xyflow/react";

import React, { useEffect, useMemo, useState } from "react";

import { Background, ReactFlow } from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import PageRenderer from "@/features/react-editor/components/PageRenderer";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { PencilLine } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { FunnelPagePopover } from "./FunnelPagePopover";

type PageData = {
  pageRoute: string;
  index: number;
  name?: string;
  id?: string;
  funnelStepId?: string;
};

const PageCanvasNode = ({
  data,
}: {
  data: {
    pageData: PageData;
    funnelStepId: string;
    isPreview?: boolean;
    projectId?: string;
    previewWidthPx: number;
    previewHeightPx: number;
  };
}) => {
  const { currentViewport } = useViewportStore();
  const { pageData, isPreview, projectId, previewWidthPx, previewHeightPx } =
    data;
  const { pageRoute, index, name, id, funnelStepId } = pageData;

  const navigate = useNavigate();

  return (
    <div
      className="border-0.5 border-border flex flex-col h-fit p-3 bg-white rounded-lg cursor-pointer group"
      onClick={() => {
        if (!isPreview) {
          navigate(`/shops/${projectId}/chat?pageId=${id}`);
        }
      }}
    >
      <div
        className="flex flex-row justify-between min-w-0"
        style={{
          width: `${previewWidthPx}px`,
        }}
      >
        <div className="flex flex-col gap-1 mb-3 min-w-0 flex-1">
          <div
            className={twMerge(
              "text-default text-base font-semibold truncate",
              currentViewport === "mobile" && "text-sm",
            )}
            title={name ?? `Page ${index + 1}`}
          >
            {name ?? `Page ${index + 1}`}
          </div>
          {!isPreview && (
            <div className="text-sm text-muted truncate" title={pageRoute}>
              {pageRoute}
            </div>
          )}
        </div>
        {!isPreview && id && funnelStepId && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <FunnelPagePopover
              pageData={{ pageId: id, pageRoute, name }}
              funnelStepId={funnelStepId}
            />
          </div>
        )}
      </div>
      <div
        className="flex justify-center w-full"
        style={{
          height: `${previewHeightPx}px`,
          width: `${previewWidthPx}px`,
        }}
      >
        <div className="border-0.5 border-border rounded-lg relative overflow-hidden w-full">
          {!isPreview && (
            <div
              className="group-hover:opacity-100 cursor-pointer absolute bg-white/50 w-full h-full flex items-center justify-center z-2 opacity-0 transition-opacity duration-200"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                navigate(`/shops/${projectId}/chat?pageId=${id}`);
              }}
            >
              <Button
                size="base"
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  navigate(`/shops/${projectId}/chat?pageId=${id}`);
                }}
              >
                <div className="flex gap-2 items-center">
                  <PencilLine size={14} /> Edit
                </div>
              </Button>
            </div>
          )}
          <PageRenderer
            pageRoute={pageRoute}
            className="h-full w-full"
            previewThumbnail={true}
          />
        </div>
      </div>
    </div>
  );
};

const nodeTypes: NodeTypes = {
  "page-canvas": PageCanvasNode,
};

interface FunnelCanvasProps {
  funnelPages: PageData[];
  isPreview?: boolean;
  projectId?: string;
}

function FunnelCanvas({
  funnelPages,
  isPreview,
  projectId,
}: FunnelCanvasProps) {
  const currentViewport = useViewportStore((state) => state.currentViewport);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  const { previewWidthPx, previewHeightPx } = useMemo(() => {
    let width: number;
    let scale: number;
    let height: number;

    switch (currentViewport) {
      case "mobile":
        scale = 0.45;
        width = CANVAS_DATA[currentViewport].defaultFrameWidth * scale;
        height = width * (19.5 / 9); // iphone 16 aspect ratio (portrait mode)
        break;
      case "tablet":
        scale = 0.35;
        width = CANVAS_DATA[currentViewport].defaultFrameWidth * scale;
        height = width;
        break;
      case "desktop":
      default:
        scale = 0.25;
        width = CANVAS_DATA[currentViewport].defaultFrameWidth * scale;
        height = width;
        break;
    }

    return { previewWidthPx: width, previewHeightPx: height };
  }, [currentViewport]);

  const nodes = useMemo(() => {
    const nodeSpacing = previewWidthPx + 50;

    return funnelPages.map((pageData, index) => ({
      id: `page-${index}`,
      type: "page-canvas",
      position: { x: index * nodeSpacing, y: 0 },
      data: {
        pageData,
        isPreview,
        projectId,
        previewWidthPx,
        previewHeightPx,
      },
    }));
  }, [funnelPages, previewWidthPx, previewHeightPx, isPreview, projectId]);

  const edges = useMemo(() => {
    return funnelPages.slice(0, -1).map((_, index) => ({
      id: `edge-${index}`,
      source: `page-${index}`,
      target: `page-${index + 1}`,
      type: "smoothstep",
      style: { stroke: "#000", strokeWidth: 2 },
    }));
  }, [funnelPages]);

  useEffect(() => {
    if (reactFlowInstance && currentViewport) {
      void reactFlowInstance.fitView();
    }
  }, [reactFlowInstance, currentViewport]);

  return (
    <div className="border-0.5 border-border h-full w-full rounded-lg overflow-hidden flex-1 bg-sheet">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        panOnScroll={true}
        panOnScrollSpeed={1}
        onInit={setReactFlowInstance}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
      </ReactFlow>
    </div>
  );
}

export default FunnelCanvas;
